import { Redirect, Route, Switch } from "wouter";
import { AppShell } from "./app/AppShell";
import { useAuth } from "./auth/AuthContext";
import { landingPathFor } from "./auth/roleView";
import { AccountPage } from "./features/account/AccountPage";
import { BillingPage } from "./features/billing/BillingPage";
import { AuditPage } from "./features/admin/audit/AuditPage";
import { UsersPage } from "./features/admin/users/UsersPage";
import { ContainerDetailPage } from "./features/containers/ContainerDetailPage";
import { ContainersListPage } from "./features/containers/ContainersListPage";
import { InstancesListPage } from "./features/instances/InstancesListPage";
import { NodesListPage } from "./features/nodes/NodesListPage";
import { ProjectDetailPage } from "./features/projects/ProjectDetailPage";
import { ProjectsListPage } from "./features/projects/ProjectsListPage";
import { ProviderCredentialsPage } from "./features/provider-credentials/ProviderCredentialsPage";
import { SecretsPage } from "./features/secrets/SecretsPage";
import { ServiceDetailPage } from "./features/services/ServiceDetailPage";
import { StackDetailPage } from "./features/stacks/StackDetailPage";
import { StacksListPage } from "./features/stacks/StacksListPage";
import { LoginPage } from "./routes/LoginPage";
import { NotFoundPage } from "./routes/NotFoundPage";
import { ProtectedRoute } from "./routes/ProtectedRoute";

/**
 * FE-M1: `ProtectedRoute`/`AppShell`/`FleetProvider` are mounted exactly
 * once here, above a single inner `<Switch>` — not per-route as before
 * (every one of the routes below used to nest its own copy). That single
 * outer mount is what keeps the shell's event subscription
 * (`useContainerEventsSubscription`, wired inside `AppShell`) alive across
 * navigation between any two protected pages: React keeps a component
 * instance mounted as long as it stays at the same position in the tree
 * with the same type, and since the outer `<Route>` matches (and its
 * `AppShell` therefore stays rendered) for every protected path, only a
 * round trip through `/login` unmounts it. Once the real per-fleet event
 * channel lands (P-1), this is what stops it from silently dropping events
 * across every navigation.
 */
/** Sends "/" to the first page the signed-in role can actually load. */
function RoleLanding() {
  const { session } = useAuth();
  return <Redirect to={landingPathFor(session?.role)} />;
}

export function App() {
  return (
    <Switch>
      <Route path="/login">
        <LoginPage />
      </Route>

      {/* No `path`: matches every route below (anything other than
          `/login`) so the shell mounts once for the whole authenticated
          app. `ProtectedRoute` still redirects to `/login` reactively if
          there's no session — unauthenticated visitors never see the shell
          around a page, matching the previous per-route behavior. */}
      <Route>
        <ProtectedRoute>
          <AppShell>
            <Switch>
              {/* Where "/" lands depends on the role: a billing_admin has no
                  fleet view to land on (it holds billing and nothing else), so
                  sending it to /containers would greet it with a 403 on sign-in.
                  See auth/roleView.ts. */}
              <Route path="/">
                <RoleLanding />
              </Route>

              <Route path="/containers">
                <ContainersListPage />
              </Route>

              {/* A container id is only unique per node (see api/client.ts), so
                  the node rides along in the URL too — deep links stay
                  resolvable without first loading the containers list. */}
              <Route path="/containers/:nodeId/:containerId">
                <ContainerDetailPage />
              </Route>

              <Route path="/stacks">
                <StacksListPage />
              </Route>

              <Route path="/stacks/:id">
                <StackDetailPage />
              </Route>

              <Route path="/nodes">
                <NodesListPage />
              </Route>

              <Route path="/secrets">
                <SecretsPage />
              </Route>

              {/* Hero flow: project -> service -> (deploy is not implemented
                  yet, see ServiceDetailPage), plus the Hetzner provisioning
                  half of the flow. Services live nested under their
                  project rather than at their own top-level route — a
                  service id is only meaningful in the context of the
                  project that owns it, same reasoning as containers nesting
                  under nodeId. */}
              <Route path="/projects">
                <ProjectsListPage />
              </Route>

              <Route path="/projects/:projectId">
                <ProjectDetailPage />
              </Route>

              <Route path="/projects/:projectId/services/:serviceId">
                <ServiceDetailPage />
              </Route>

              <Route path="/provider-credentials">
                <ProviderCredentialsPage />
              </Route>

              <Route path="/instances">
                <InstancesListPage />
              </Route>

              {/* Admin/user portal: Users + Audit log are admin-gated inside
                  each page (AdminGate) — see NavSidebar for the matching
                  UX-only nav gate. Account is reachable by every signed-in
                  role (linked from TopBar, not NavSidebar). */}
              <Route path="/admin">
                <Redirect to="/admin/users" />
              </Route>

              <Route path="/admin/users">
                <UsersPage />
              </Route>

              <Route path="/admin/audit">
                <AuditPage />
              </Route>

              <Route path="/account">
                <AccountPage />
              </Route>

              {/* Billing (L-1). Reachable by admin and billing_admin; the page
                  itself shows a "no access" state for anyone else, and the
                  server enforces the real decision (S-8). */}
              <Route path="/billing">
                <BillingPage />
              </Route>

              {/* No `path`: matches whatever falls through the routes above,
                  same as react-router's catch-all `*`. */}
              <Route>
                <NotFoundPage />
              </Route>
            </Switch>
          </AppShell>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}
